import { Box } from '@mui/material';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from '../../../constants/appRoutes';
import { CardSpacing } from '../../../constants/cardSpacing';
import { FormHelper } from '../../../helpers/formHelper';
import { getCultureDisplayName } from '../../../helpers/localeHelper';
import ActionBarDetail from '../../../shared/components/common/actionBarDetail';
import SectionWithHeading from '../../../shared/components/common/sectionWithHeading';
import { Spacing } from '../../../shared/components/common/spacing';
import InputWrapper from '../../../shared/formUtilities/useInput';
import { StaticPageSchema } from '../../../shared/validations/schema';
import { StaticPageService } from '../staticPageService';
import { buildRegionizeRoute } from '../../../helpers/regionRouteHelper';
import { CkRichEditor } from '../../../shared/formUtilities/useRichEditor';

export const StaticPageCreate = () => {
	const staticPageService = new StaticPageService();
	const [staticPageDetail, setStaticPageDetail] = useState({});
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const slug = useParams()?.slug;
	const initialValues = {
		slug: '',
		languages: FormHelper.createLanguageFormArray({
			withTitle: true,
			withDescription: true,
		}),
	};

	const form = useFormik({
		initialValues: initialValues,
		validationSchema: StaticPageSchema,
		onSubmit: () => {
			if (form.isValid) {
				onSaveOrUpdate();
			}
		},
		...FormHelper.formikProps(),
	});

	const isArabic = (language) => {
		return getCultureDisplayName(language?.culture) === 'Arabic';
	};

	useEffect(() => {
		if (slug) {
			setLoading(true);
			getDetail();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slug]);

	const onEdit = (event) => {
		event.preventDefault();
		FormHelper.validateForm(form);
	};

	const onUpdate = () => {
		staticPageService
			.updateStaticPage(staticPageDetail?.id, form.values)
			.then((resp) => {
				setLoading(false);
				onNavigate();
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const onSave = () => {
		staticPageService
			.addStaticPage(form.values)
			.then((resp) => {
				setLoading(false);
				onNavigate();
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const onNavigate = () => {
		navigate(buildRegionizeRoute(`${AppRoutes.StaticPages}`));
	};

	const onSaveOrUpdate = () => {
		setLoading(true);
		if (slug) {
			onUpdate();
		} else {
			onSave();
		}
	};

	const cultureName = (language) => {
		return getCultureDisplayName(language?.culture);
	};

	const getDetail = () => {
		staticPageService
			.getStaticPageDetail(slug)
			.then((resp) => {
				setStaticPageDetail(resp.data);
				form.setValues(resp.data);
				if (!resp?.data?.languages?.length) {
					form.setFieldValue(
						'languages',
						FormHelper.createLanguageFormArray({
							withTitle: true,
							withDescription: true,
						})
					);
				}
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	return (
		<FormikProvider value={form}>
			<ActionBarDetail
				loading={loading}
				userName={`${
					staticPageDetail?.slug || 'Add Static Page'
				}`}></ActionBarDetail>
			<Spacing></Spacing>
			<SectionWithHeading
				loading={loading}
				heading={'Static Page Template'}
				isEditButton={true}
				onEditInfo={(e) => {
					onEdit(e);
				}}
				isEditToggle={true}
				children={
					<Box className="grid grid-flow-row">
						<fieldset style={{ height: 'fit-content' }}>
							<InputWrapper
								name={'slug'}
								key={'slug'}
								label={'Slug'}></InputWrapper>
						</fieldset>
						<Spacing space={CardSpacing.lg}></Spacing>
						<fieldset>
							<FieldArray name="languages">
								<Box
									className="grid grid-cols-1 md:grid-cols-2 gap-4"
									style={{ height: 'fit-content' }}>
									{form.values.languages.length > 0 &&
										form.values.languages.map((language, index) => {
											return (
												<SectionWithHeading
													key={index}
													heading={`${cultureName(language)}`}
													children={
														<Box className="flex flex-0 flex-col justify-start space-y-3">
															<InputWrapper
																name={`languages[${index}].title`}
																key={`languages[${index}].title`}
																label={'Title'}></InputWrapper>
															<CkRichEditor
																isArabic={isArabic(language)}
																key={index}
																value={language?.description}
																onChange={(data) => {
																	form.setFieldValue(
																		`languages[${index}].description`,
																		data
																	);
																}}></CkRichEditor>
														</Box>
													}></SectionWithHeading>
											);
										})}
								</Box>
							</FieldArray>
						</fieldset>
					</Box>
				}></SectionWithHeading>
		</FormikProvider>
	);
};
